import type { CatalogItem, CatalogItemInput, FormTemplateView, GfeSettingsView, ManagedItemType, RoleItem } from "@vaulted/shared";
import { CHART_SECTIONS, CHART_SECTION_LABELS, ITEM_TYPE_LABELS, totalMinutes } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { errorMessage, get, post, put } from "../api";
import { Alert, Badge, Button, Checkbox, Field, Loading, MinutesField, MoneyField, Panel, PanelHeader, SelectField, TextAreaField, formatMinutes, money } from "../components/ui";
import { useSession } from "../session";
import { type CatalogResponse, TYPE_PATHS, useCatalog } from "./Catalog";
import { NeedsPanel } from "./Needs";
import { BuildSheet, RecipePanel } from "./Recipe";
import { TierEditor } from "./settings/BookingTab";
import { BackLink } from "../components/BackLink";
import { PhotoInput } from "../components/PhotoInput";

const PATH_TYPES: Record<string, ManagedItemType> = { services: "service", "add-ons": "add_on", products: "product" };
const COLORS = ["#C51A6A", "#7A4FB5", "#2F7D6D", "#2B6CB0", "#C05621", "#B7791F", "#4A5568"];

const blank = (type: ManagedItemType): CatalogItemInput => ({
  type,
  name: "",
  description: "",
  categoryId: null,
  showOnPublicMenu: true,
  availableClinic: true,
  availableMobile: true,
  runsAlongside: false,
  priceCents: 0,
  mobilePriceCents: null,
  eventPriceCents: null,
  taxable: type === "product",
  startMinutes: type === "service" ? 15 : 0,
  workingMinutes: type === "service" ? 30 : 0,
  cleanupMinutes: 0,
  color: type === "service" ? COLORS[0]! : null,
  allowedRoleIds: [],
  groupDiscountMode: "default",
  groupDiscountCustom: { clinic: [], mobile: [], event: [] },
  attachToCategoryIds: [],
  attachToServiceIds: [],
  sku: null,
  chartSection: null,
  requiredFormKeys: null,
  needsGfe: false,
  gfeExamId: null,
});

const toInput = (i: CatalogItem): CatalogItemInput => ({
  type: i.type as ManagedItemType,
  name: i.name,
  description: i.description,
  categoryId: i.categoryId,
  showOnPublicMenu: i.showOnPublicMenu,
  availableClinic: i.availableClinic,
  availableMobile: i.availableMobile,
  runsAlongside: i.runsAlongside,
  priceCents: i.priceCents,
  mobilePriceCents: i.mobilePriceCents,
  eventPriceCents: i.eventPriceCents,
  taxable: i.taxable,
  startMinutes: i.startMinutes,
  workingMinutes: i.workingMinutes,
  cleanupMinutes: i.cleanupMinutes,
  color: i.color,
  allowedRoleIds: i.allowedRoleIds,
  groupDiscountMode: i.groupDiscountMode,
  groupDiscountCustom: i.groupDiscountCustom,
  attachToCategoryIds: i.attachToCategoryIds,
  attachToServiceIds: i.attachToServiceIds,
  sku: i.sku,
  chartSection: i.chartSection,
  requiredFormKeys: i.requiredFormKeys,
  needsGfe: i.needsGfe,
  gfeExamId: i.gfeExamId,
});

export function CatalogItemPage() {
  const { id, type: typePath } = useParams();
  const catalog = useCatalog();
  if (catalog.isLoading) return <Loading />;
  if (catalog.error || !catalog.data) return <Alert>{errorMessage(catalog.error)}</Alert>;
  const existing = id ? catalog.data.items.find((i) => i.id === id) : undefined;
  if (id && !existing) return <Alert>That item doesn't exist.</Alert>;
  const type = existing ? (existing.type as ManagedItemType) : PATH_TYPES[typePath ?? ""];
  if (!type) return <Alert>Unknown item type.</Alert>;
  return <ItemForm key={id ?? typePath} type={type} item={existing} data={catalog.data} />;
}

function ItemForm({ type, item, data }: { type: ManagedItemType; item?: CatalogItem; data: CatalogResponse }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const manage = can("catalog.manage");
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => get<RoleItem[]>("/api/roles"), enabled: manage && type === "service" });
  const initial = item ? toInput(item) : blank(type);
  const [v, setV] = useState<CatalogItemInput>(initial);
  // A just-created item arrives with its "saved" message in the navigation state.
  const [notice, setNotice] = useState<string | null>((location.state as { notice?: string } | null)?.notice ?? null);
  useEffect(() => {
    if (item) setV(toInput(item));
  }, [item]);
  const set = <K extends keyof CatalogItemInput>(k: K, value: CatalogItemInput[K]) => {
    setNotice(null);
    setV((cur) => ({ ...cur, [k]: value }));
  };
  const toggle = (k: "allowedRoleIds" | "attachToCategoryIds" | "attachToServiceIds", id: string, on: boolean) =>
    set(k, on ? [...v[k], id] : v[k].filter((x) => x !== id));

  const labels = ITEM_TYPE_LABELS[type];
  const listPath = `/catalog/${TYPE_PATHS[type]}`;
  const save = useMutation({
    mutationFn: () => (item ? put<CatalogItem>(`/api/catalog/items/${item.id}`, v) : post<CatalogItem>("/api/catalog/items", v)),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ["catalog"] });
      if (!item) navigate(`/catalog/item/${saved.id}`, { replace: true, state: { notice: `${labels.one} added.` } });
      else setNotice(`${labels.one} saved.`);
    },
  });
  const archive = useMutation({
    mutationFn: () => post<CatalogItem>(`/api/catalog/items/${item!.id}/${item!.status === "active" ? "archive" : "restore"}`),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ["catalog"] });
      setNotice(saved.status === "archived" ? "Archived. It no longer appears on menus or for booking." : "Restored.");
    },
  });
  const duplicate = useMutation({
    mutationFn: () => post<CatalogItem>(`/api/catalog/items/${item!.id}/duplicate`),
    onSuccess: async (copy) => {
      await qc.invalidateQueries({ queryKey: ["catalog"] });
      navigate(`/catalog/item/${copy.id}`, { state: { notice: `Copy of ${item!.name}. It's hidden from the public menu until you turn that on.` } });
    },
  });
  const dirty = JSON.stringify(v) !== JSON.stringify(initial);
  const services = data.items.filter((i) => i.type === "service" && (i.status === "active" || v.attachToServiceIds.includes(i.id)));

  return (
    <div className="max-w-3xl">
      <BackLink to={listPath} label={labels.many} />
      <header className="mt-2 mb-6 flex flex-wrap items-center gap-3">
        <h1 className="page-title">{item ? item.name : `New ${labels.one.toLowerCase()}`}</h1>
        {item?.status === "archived" && <Badge tone="off">Archived</Badge>}
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <fieldset disabled={!manage} className="flex flex-col gap-6">
          <Section title="Details">
            <Field label="Name" required maxLength={80} value={v.name} onChange={(e) => set("name", e.target.value)} />
            <SelectField label="Category" value={v.categoryId ?? ""} onChange={(e) => set("categoryId", e.target.value || null)}>
              <option value="">No category</option>
              {data.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </SelectField>
            <TextAreaField label="Description" maxLength={1000} hint="Shown to clients on the menu and booking pages." value={v.description} onChange={(e) => set("description", e.target.value)} />
            <div className="grid gap-x-4 sm:grid-cols-2">
              <Checkbox label="Offered in the clinic" checked={v.availableClinic} onChange={(on) => set("availableClinic", on)} />
              <Checkbox label="Offered on mobile visits" checked={v.availableMobile} onChange={(on) => set("availableMobile", on)} />
              {!v.availableClinic && !v.availableMobile && <p className="text-sm text-muted">Events only: offered just on the event menus you add it to.</p>}
              {v.type === "service" && (
                <Checkbox
                  label="Can run alongside other services"
                  description="On a visit with other services, this runs at the same time (compression or an injection during a drip). Otherwise services run one after another."
                  checked={v.runsAlongside}
                  onChange={(on) => set("runsAlongside", on)}
                />
              )}
              <Checkbox label="Show on the public menu" description="Turn off for staff-only items." checked={v.showOnPublicMenu} onChange={(on) => set("showOnPublicMenu", on)} />
              {item && manage && (
                <PhotoInput
                  key={item.id}
                  label="Photo for your website (the website feed)"
                  url={item.photoKey ? `/api/catalog/items/${item.id}/photo?v=${encodeURIComponent(item.photoKey.slice(-12))}` : null}
                  onChange={async (photoKey) => {
                    await put(`/api/catalog/items/${item.id}/photo`, { photoKey });
                    await qc.invalidateQueries({ queryKey: ["catalog"] });
                  }}
                />
              )}
            </div>
          </Section>

          <Section title="Pricing">
            <div className="grid gap-4 sm:grid-cols-3">
              <MoneyField label="Clinic price" cents={v.priceCents} onChange={(c) => set("priceCents", c ?? 0)} />
              <MoneyField label="Mobile price" optional placeholder="Same as clinic" cents={v.mobilePriceCents} onChange={(c) => set("mobilePriceCents", c)} />
              <MoneyField label="Event price" optional placeholder="Same as clinic" hint="Events can set their own prices later." cents={v.eventPriceCents} onChange={(c) => set("eventPriceCents", c)} />
            </div>
            <Checkbox label="Taxable" description="Charged sales tax at the location's rate." checked={v.taxable} onChange={(on) => set("taxable", on)} />
          </Section>

          {type === "service" && (
            <Section
              title="Timing"
              description="Start time needs the nurse and a chair. Working time (like drip time) needs only the chair, so the nurse can start someone else."
            >
              <div className="grid gap-4 sm:grid-cols-3">
                <MinutesField label="Start time" hint="Nurse and chair busy" value={v.startMinutes} onChange={(m) => set("startMinutes", m)} />
                <MinutesField label="Working time" hint="Chair busy, nurse free" value={v.workingMinutes} onChange={(m) => set("workingMinutes", m)} />
                <MinutesField label="Cleanup time" hint="Chair busy after the client leaves" value={v.cleanupMinutes} onChange={(m) => set("cleanupMinutes", m)} />
              </div>
              <TimingBar start={v.startMinutes} working={v.workingMinutes} cleanup={v.cleanupMinutes} />
            </Section>
          )}

          {type === "add_on" && (
            <Section title="Attaches to" description="Which services this can be added to.">
              <MinutesField className="max-w-64" label="Time it adds" hint="Added to the service's working time" value={v.workingMinutes} onChange={(m) => set("workingMinutes", m)} />
              <CheckList
                title="Every service in"
                options={data.categories.map((c) => ({ id: c.id, label: c.name }))}
                selected={v.attachToCategoryIds}
                onToggle={(id, on) => toggle("attachToCategoryIds", id, on)}
              />
              <CheckList
                title="Or these services"
                options={services.filter((s) => !s.categoryId || !v.attachToCategoryIds.includes(s.categoryId)).map((s) => ({ id: s.id, label: s.name }))}
                selected={v.attachToServiceIds}
                onToggle={(id, on) => toggle("attachToServiceIds", id, on)}
              />
            </Section>
          )}

          {type === "service" && (
            <>
              <Section title="Who can perform it">
                {roles.isLoading ? (
                  <Loading />
                ) : (
                  <CheckList options={(roles.data ?? []).map((r) => ({ id: r.id, label: r.name }))} selected={v.allowedRoleIds} onToggle={(id, on) => toggle("allowedRoleIds", id, on)} columns />
                )}
                {v.allowedRoleIds.length === 0 && <p className="text-sm text-wait">No one can be booked for this until you pick at least one role.</p>}
              </Section>

              <Section title="Group discount">
                <SelectField label="For groups" value={v.groupDiscountMode} onChange={(e) => set("groupDiscountMode", e.target.value as CatalogItemInput["groupDiscountMode"])}>
                  <option value="default">Use the clinic's group discounts</option>
                  <option value="custom">Use its own discounts</option>
                  <option value="none">No group discount</option>
                </SelectField>
                {v.groupDiscountMode === "custom" && (
                  <div className="flex flex-col gap-4">
                    <TierEditor title="Clinic" tiers={v.groupDiscountCustom.clinic} onChange={(t) => set("groupDiscountCustom", { ...v.groupDiscountCustom, clinic: t })} />
                    <TierEditor title="Mobile" tiers={v.groupDiscountCustom.mobile} onChange={(t) => set("groupDiscountCustom", { ...v.groupDiscountCustom, mobile: t })} />
                    <TierEditor title="Events" tiers={v.groupDiscountCustom.event} onChange={(t) => set("groupDiscountCustom", { ...v.groupDiscountCustom, event: t })} />
                  </div>
                )}
              </Section>

              <Section title="Calendar color">
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => set("color", c)}
                      className="size-8 rounded-full ring-offset-2 aria-pressed:ring-2 aria-pressed:ring-ink"
                      style={{ background: c }}
                      aria-pressed={v.color === c}
                      aria-label={`Color ${c}`}
                    />
                  ))}
                </div>
              </Section>
            </>
          )}

          {(type === "service" || type === "add_on") && <ChartingSection type={type} v={v} item={item} set={set} />}

          {type === "product" && (
            <Section title="Product">
              <Field label="SKU (optional)" maxLength={60} value={v.sku ?? ""} onChange={(e) => set("sku", e.target.value || null)} className="max-w-64" />
            </Section>
          )}
        </fieldset>

        {manage && (
          <div className="sticky bottom-0 mt-6 flex flex-col gap-3 border-t border-line bg-paper/95 py-4 backdrop-blur">
            {(save.error || archive.error || duplicate.error) && <Alert>{errorMessage(save.error ?? archive.error ?? duplicate.error)}</Alert>}
            {notice && !dirty && <Alert tone="ok">{notice}</Alert>}
            <div className="flex flex-wrap items-center justify-between gap-3">
              {item ? (
                <div className="flex gap-2">
                  <Button type="button" variant={item.status === "active" ? "danger" : "secondary"} busy={archive.isPending} onClick={() => archive.mutate()}>
                    {item.status === "active" ? "Archive" : "Restore"}
                  </Button>
                  <Button
                    type="button"
                    busy={duplicate.isPending}
                    disabled={dirty}
                    title={dirty ? "Save or undo your changes first" : "Make a copy to edit"}
                    onClick={() => duplicate.mutate()}
                  >
                    Duplicate
                  </Button>
                </div>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-3">
                <span className="hidden text-sm text-muted sm:inline">
                  {money(v.priceCents)}
                  {type === "service" && ` · ${formatMinutes(totalMinutes(v))}`}
                </span>
                <Button variant="primary" type="submit" busy={save.isPending} disabled={!dirty}>
                  {item ? "Save changes" : `Add ${labels.one.toLowerCase()}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </form>

      {(type === "service" || type === "add_on") && (
        <div className="mt-6 flex flex-col gap-6">
          {item ? (
            <>
              <NeedsPanel item={item} />
              <RecipePanel item={item} data={data} />
              {type === "service" && <BuildSheet item={item} data={data} />}
            </>
          ) : (
            <Alert tone="info">Add the {labels.one.toLowerCase()} first, then you can enter its recipe.</Alert>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <Panel>
      <PanelHeader title={title} description={description} />
      <div className="flex flex-col gap-4 px-5 py-5">{children}</div>
    </Panel>
  );
}

function CheckList({
  title,
  options,
  selected,
  onToggle,
  columns,
}: {
  title?: string;
  options: { id: string; label: string }[];
  selected: string[];
  onToggle: (id: string, on: boolean) => void;
  columns?: boolean;
}) {
  return (
    <fieldset>
      {title && <legend className="mb-1 text-sm font-semibold text-ink-soft">{title}</legend>}
      <div className={columns ? "grid gap-x-4 sm:grid-cols-2 lg:grid-cols-3" : "flex flex-col"}>
        {options.map((o) => (
          <Checkbox key={o.id} label={o.label} checked={selected.includes(o.id)} onChange={(on) => onToggle(o.id, on)} />
        ))}
      </div>
    </fieldset>
  );
}

/** A picture of who's busy when: nurse during start time, chair the whole time. */
function TimingBar({ start, working, cleanup }: { start: number; working: number; cleanup: number }) {
  const total = start + working + cleanup;
  if (!total) return null;
  const pct = (m: number) => `${(m / total) * 100}%`;
  return (
    <div className="flex flex-col gap-1.5 text-xs">
      <div className="flex items-center gap-2">
        <span className="w-12 text-muted">Nurse</span>
        <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-mist">
          <div className="bg-brand" style={{ width: pct(start) }} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-12 text-muted">Chair</span>
        <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-mist">
          <div className="bg-ink" style={{ width: pct(start + working + cleanup) }} />
        </div>
      </div>
      <p className="text-sm text-ink-soft">
        Client sees {formatMinutes(start + working)}
        {cleanup > 0 && `, plus ${formatMinutes(cleanup)} cleanup`}. The nurse is free after {formatMinutes(start)}.
      </p>
    </div>
  );
}

/** Where the item shows on nurse charts, and (services) which forms clients sign first. */
function ChartingSection({ type, v, item, set }: { type: ManagedItemType; v: CatalogItemInput; item?: CatalogItem; set: <K extends keyof CatalogItemInput>(k: K, value: CatalogItemInput[K]) => void }) {
  const forms = useQuery({ queryKey: ["form-templates"], queryFn: () => get<FormTemplateView[]>("/api/form-templates"), enabled: type === "service" });
  const auto = item ? CHART_SECTION_LABELS[item.autoChartSection] : "automatic";
  const keys = v.requiredFormKeys;
  return (
    <Section title="Charting and forms" description="Charts list catalog items by section; what was booked is pre-checked for the nurse.">
      <SelectField label="On charts" className="max-w-md" value={v.chartSection ?? ""} onChange={(e) => set("chartSection", (e.target.value || null) as CatalogItemInput["chartSection"])}>
        <option value="">Automatic ({auto})</option>
        {CHART_SECTIONS.map((c) => (
          <option key={c} value={c}>
            {CHART_SECTION_LABELS[c]}
          </option>
        ))}
      </SelectField>
      {type === "service" && <GfeExamField needsGfe={v.needsGfe} examId={v.gfeExamId} onChange={(needsGfe, gfeExamId) => { set("needsGfe", needsGfe); set("gfeExamId", gfeExamId); }} />}
      {type === "service" && forms.data && (
        <fieldset>
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Forms clients sign before this service</legend>
          <Checkbox label="Every form (recommended)" checked={keys === null} onChange={(on) => set("requiredFormKeys", on ? null : forms.data.map((f) => f.key))} />
          {keys !== null && (
            <div className="ml-6">
              {forms.data.map((f) => (
                <Checkbox key={f.key} label={f.name} checked={keys.includes(f.key)} onChange={(on) => set("requiredFormKeys", on ? [...keys, f.key] : keys.filter((k) => k !== f.key))} />
              ))}
            </div>
          )}
        </fieldset>
      )}
    </Section>
  );
}

/** Which good faith exam a service needs: none, the clinic's default, or a specific Qualiphy exam. */
function GfeExamField({ needsGfe, examId, onChange }: { needsGfe: boolean; examId: number | null; onChange: (needsGfe: boolean, examId: number | null) => void }) {
  // Qualiphy's exam list (only for people who manage settings; others see the saved choice).
  const gfe = useQuery({ queryKey: ["gfe-settings"], queryFn: () => get<GfeSettingsView>("/api/gfe/settings"), retry: false, staleTime: 5 * 60_000 });
  const exams = gfe.data?.exams ?? [];
  const value = !needsGfe ? "none" : examId == null ? "default" : String(examId);
  return (
    <SelectField
      label="Good faith exam"
      className="max-w-xl"
      hint="The client needs a current approval for this exam (or the medical director's tag) on the visit date. It's sent automatically when the visit is booked."
      value={value}
      onChange={(e) => {
        const x = e.target.value;
        onChange(x !== "none", x === "none" || x === "default" ? null : Number(x));
      }}
    >
      <option value="none">Not needed</option>
      <option value="default">Clinic default (Settings → Good faith exams)</option>
      {examId != null && !exams.some((x) => x.id === examId) && <option value={examId}>#{examId}</option>}
      {exams.map((x) => (
        <option key={x.id} value={x.id}>
          #{x.id} {x.title}
        </option>
      ))}
    </SelectField>
  );
}
